import { FC } from 'react';
import { FieldHelperProps, FieldInputProps, FieldMetaProps } from 'formik';
import { Option } from 'components/ui/base/select/Select';
import { DutyPayer, dutyPayerLabels } from 'store/customers/customer';
import { StyledDeclarationSelect } from 'components/ui/composed/declarations/formSelect/DeclarationSelect.styles';
import {
    ErrorDiv,
    ErrorLabel,
    FieldWrapper,
    FormItem,
    InputDiv,
    InputLabel,
    InputLabelRequired,
    InputSection,
    LabelDiv,
    LabelSpacing,
    MetaSection,
} from 'components/ui/composed/declarations/formInput/DeclarationInput.styles';
import { DutyPayerOptionRow, OptionTitle, TruncatedOptionLabel } from '../styles';
import DotSeparator from 'components/new-ui/base/dot-separator/DotSeparator';
import { StyledRef } from 'components/new-ui/base/ref/styles';
import EllipsisTextTooltip from 'components/EllipsisTextTooltip';

export interface DutyPayerOption {
    id: DutyPayer;
    title: string;
    label: string;
    eori: string;
    referenceCode?: string;
}

// Builds the standard "Declarant pays" / "Party pays" duty payer options from the
// tenant (declarant) EORI and the party being edited's EORI.
export const getDutyPayerOptions = (tenantEori?: string, partyEori?: string): DutyPayerOption[] => [
    {
        id: DutyPayer.DECLARANT,
        title: dutyPayerLabels[DutyPayer.DECLARANT],
        label: 'Declarant pays',
        eori: tenantEori ?? '',
        referenceCode: '3.46',
    },
    {
        id: DutyPayer.CONSIGNEE,
        title: dutyPayerLabels[DutyPayer.CONSIGNEE],
        label: 'Party pays',
        eori: partyEori ?? '',
        referenceCode: '3.46',
    },
];

interface Props {
    label?: string;
    labelSpacing?: LabelSpacing;
    required?: boolean;
    condensed?: boolean;
    options: DutyPayerOption[];
    fieldProps: FieldInputProps<any>;
    fieldMeta?: FieldMetaProps<any>;
    fieldHelper?: FieldHelperProps<any>;
}

const DutyPayerSelect: FC<Props> = ({
    label,
    labelSpacing = 'none',
    required,
    condensed,
    options,
    fieldProps,
    fieldMeta,
    fieldHelper,
}) => {
    const id = `select-id-${fieldProps.name}`;

    return (
        <FieldWrapper $viewOnly={false} $template={false}>
            {label && (
                <MetaSection>
                    <LabelDiv condensed={condensed} $labelSpacing={labelSpacing}>
                        <InputLabel htmlFor={id}>
                            {required && <InputLabelRequired right>*</InputLabelRequired>}
                            {label}
                        </InputLabel>
                    </LabelDiv>
                </MetaSection>
            )}

            <InputSection>
                <FormItem validateStatus={fieldMeta?.error && !!fieldMeta?.touched ? 'error' : ''}>
                    <InputDiv>
                        <StyledDeclarationSelect
                            id={id}
                            allowClear
                            condensed={condensed}
                            lock={false}
                            size={condensed ? 'middle' : 'large'}
                            optionLabelProp="label"
                            placeholder="Select Duty Payer "
                            value={fieldProps.value || undefined}
                            onChange={(value) => fieldHelper?.setValue(value)}
                            onBlur={() => fieldHelper?.setTouched(true)}
                            status={fieldMeta?.error && !!fieldMeta?.touched ? 'error' : ''}
                        >
                            {options.map((option) => (
                                <Option key={option.id} value={option.id} label={option.title}>
                                    <OptionTitle>{option.title}</OptionTitle>
                                    <DutyPayerOptionRow>
                                        <span>{option.label}</span>
                                        <DotSeparator />
                                        <EllipsisTextTooltip
                                            tooltipTitle={`${option.label} EORI: ${option.eori} ${option.referenceCode}`}
                                        >
                                            <TruncatedOptionLabel>{`EORI: ${option.eori}`}</TruncatedOptionLabel>
                                        </EllipsisTextTooltip>
                                        <DotSeparator />
                                        <StyledRef>{option.referenceCode}</StyledRef>
                                    </DutyPayerOptionRow>
                                </Option>
                            ))}
                        </StyledDeclarationSelect>
                    </InputDiv>
                    <ErrorDiv error={!!fieldMeta?.error} touched={!!fieldMeta?.touched} condensed={condensed}>
                        {!!fieldMeta?.error && !!fieldMeta?.touched && <ErrorLabel>{fieldMeta?.error}</ErrorLabel>}
                    </ErrorDiv>
                </FormItem>
            </InputSection>
        </FieldWrapper>
    );
};

export default DutyPayerSelect;
